import * as queryString from 'query-string'
import * as crypto from 'crypto'
import typeChecker from '../typeChecker'
import { Product } from '../types/entities/api/product'
import { getTRCodeByNaPm } from 'common/services/logeye'
import { isBrowser } from 'common/utils'
import history from 'common/history'
import logger from 'common/logger'
import { isServer } from '@shopping/common'

declare const wcs
declare const wcs_add
declare const wcs_do

export enum InflowRoute {
  DEFAULT = '',
  LIST = 'list',
  DETAIL = 'detail',
  NEWS_DETAIL = 'newsDetail',
  AITEMS = 'aiTems',
  SPECIAL_EVENT = 'spacialEvent',
  HOF = 'hof',
  QR = 'qr',
  DISPLAY = 'display',
  MY_REVIEW = 'myReview',
}

export enum TRCode {
  SWN = 'swn',
  SWL = 'swl',
  SWD = 'swd',
  SWNN = 'swnn',
  SWNE = 'swne',
  SWNP = 'swnp',
  SWQR = 'swqr',
  SWVD = 'swvd',
  SWDA = 'swda',
  MYV = 'myv',
  GDR = 'gdr',
  GLR = 'glr',
  GDP = 'gdp',
  GLP = 'glp',
  MD = 'md',
}

const PRIVATE_KEY_FOR_NAPM = 'e6f6cf0ab99636ae8e52e87200fd59714c5bcc54'

export const makeNaverAnalyticsParameter = (inflowRoute: InflowRoute, NNB?: string, inflowCode?: string) => {
  const ct = getClickTimeForNaPm()
  const tr = getTrCode(inflowRoute)
  const nnb = NNB || (isBrowser && getNNBOfCookie(document.cookie))
  if (['wsqr', 'wlqr'].includes(inflowCode)) {
    return `${encodeURIComponent(
      `ct=${ct}|ci=shoppingwindow|tr=swqr|hk=${makeHashKeyForNaPm(ct, TRCode.SWQR, nnb)}|trx=`,
    )}`
  }
  return `${encodeURIComponent(`ct=${ct}|ci=shoppingwindow|tr=${tr}|hk=${makeHashKeyForNaPm(ct, tr, nnb)}|trx=`)}`
}

export const makeNaPmWithTRCode = (trCode: string, NNB?: string, trx?: string) => {
  if (!trCode || trCode === TRCode.SWN) return null
  const ct = getClickTimeForNaPm()
  const nnb = NNB || (isBrowser && getNNBOfCookie(document.cookie))
  if (['wsqr', 'wlqr'].includes(trCode)) {
    return `${encodeURIComponent(
      `ct=${getClickTimeForNaPm()}|ci=shoppingwindow|tr=swqr|hk=${makeHashKeyForNaPm(ct, 'swqr', nnb)}|trx=${trx ||
        ''}`,
    )}`
  }
  return `${encodeURIComponent(
    `ct=${ct}|ci=shoppingwindow|tr=${trCode}|hk=${makeHashKeyForNaPm(ct, trCode, nnb)}|trx=${trx || ''}`,
  )}`
}

export const setNaverAnalytics = (product: Product) => {
  if (!wcs) return
  const { ssChannel, category } = product
  wcs_add['wa'] = ssChannel.naSiteId
  wcs.inflow()
  if (typeChecker.isFunction(wcs_do)) {
    const _nao = {}
    _nao['mid'] = ssChannel.naverPayNo
    _nao['chno'] = ssChannel.id
    _nao['mtyp'] = 'SWN'
    _nao['vtyp'] = 'DET'
    _nao['pid'] = product.id
    _nao['pnm'] = product.windowProduct.name

    if (category.category1Id && category.category1Name) {
      _nao['lcatid'] = category.category1Id
      _nao['lcatnm'] = category.category1Name
    }

    if (category.category2Id && category.category2Name) {
      _nao['mcatid'] = category.category2Id
      _nao['mcatnm'] = category.category2Name
    }

    if (category.category3Id && category.category3Name) {
      _nao['scatid'] = category.category3Id
      _nao['scatnm'] = category.category3Name
    }

    if (category.category4Id && category.category4Name) {
      _nao['dcatid'] = category.category4Id
      _nao['dcatnm'] = category.category4Name
    }

    wcs_do(_nao)
  }
}

export const getTrCode = (inflowRoute: InflowRoute) => {
  switch (inflowRoute) {
    case InflowRoute.LIST:
      return TRCode.SWL
    case InflowRoute.DETAIL:
      return TRCode.SWD
    case InflowRoute.NEWS_DETAIL:
      return TRCode.SWNN
    case InflowRoute.SPECIAL_EVENT:
      return TRCode.SWNE
    case InflowRoute.HOF:
      return TRCode.SWNP
    case InflowRoute.QR:
      return TRCode.SWQR
    case InflowRoute.DISPLAY:
      return TRCode.SWVD
    case InflowRoute.AITEMS:
      return TRCode.SWDA
    case InflowRoute.MY_REVIEW:
      return TRCode.MYV
    default:
      return TRCode.SWN
  }
}

export const getCheckoutInflowCode = (channelId: string) => {
  if (!wcs || !wcs.getInflowRoute) return null
  const ct = getClickTimeForNaPm()
  const queryParams = queryString.parse(window.location.search)
  const { NaPm } = queryParams
  const { trx = '', tr } = splitNaPm(NaPm)
  if (!wcs.getInflowRoute()) {
    return encodeURIComponent(`ct=${ct}|ci=DUMMY|tr=${tr}|hk=dummy|trx=${trx}`)
  }
  return encodeURIComponent(`ct=${ct}|ci=DUMMY|tr=${wcs.getInflowRoute() || ''}|hk=dummy|trx=${trx}`)
}

export const reloadNaverAnalytics = (product: Product) => {
  if (!isBrowser) return

  const scripts = document.getElementsByTagName('script')
  Array.from(scripts)
    .filter(script => {
      const { src } = script
      if (!src) return false
      return src.includes('https://wcs.naver.net/wcslog.js')
    })
    .forEach(script => {
      script.parentNode.removeChild(script)
    })
  const script = document.createElement('script')
  script.onload = () => {
    setNaverAnalytics(product)
  }
  script.src = 'https://wcs.naver.net/wcslog.js'
  document.head.appendChild(script)
}

export const excludeLogeyeTrCode = (trCode: string): boolean => {
  return !trCode || trCode.endsWith('_myc') || trCode.endsWith('_myz') || trCode.endsWith('_myr')
}

export const getClickTimeForNaPm = () => {
  return new Date().getTime().toString(36)
}

export const isValidHashKey = (naPm: string, NNB: string) => {
  const { ci, ct, tr, hk } = splitNaPm(naPm)
  const hashKeyToCompare = crypto
    .createHash('sha1')
    .update(PRIVATE_KEY_FOR_NAPM)
    .update(concatHashSource(ci, ct, tr, NNB))
    .digest('hex')
  return hashKeyToCompare === hk
}

export const concatHashSource = (ci: string, ct: string, tr: string, NNB: string) => {
  return `${ci}${ct}${tr}${NNB}`
}

export const makeHashKeyForNaPm = (ct: string, tr: string, NNB: string, ci?: string) => {
  const default_ci = 'shoppingwindow'
  return crypto
    .createHash('sha1')
    .update(PRIVATE_KEY_FOR_NAPM)
    .update(`${ci || default_ci}${ct}${tr}${NNB}`)
    .digest('hex')
}

export const splitNaPm = (naPm: string) => {
  if (!naPm) return {}
  let splitNaPm
  if (typeChecker.isArray(naPm)) {
    isServer && logger.error(`NaPm is Array Format : ${naPm}`)
    splitNaPm = naPm[0].split('|')
  } else {
    splitNaPm = naPm.split('|')
  }
  const ci = splitNaPm
    .filter(str => str.startsWith('ci='))
    .map(str => {
      return str ? str.substr(3) : ''
    })
    .pop()
  const ct = splitNaPm
    .filter(str => str.startsWith('ct='))
    .map(str => {
      return str ? str.substr(3) : ''
    })
    .pop()
  const tr = splitNaPm
    .filter(str => str.startsWith('tr='))
    .map(str => {
      return str ? str.substr(3) : ''
    })
    .pop()
  const hk = splitNaPm
    .filter(str => str.startsWith('hk='))
    .map(str => {
      return str ? str.substr(3) : ''
    })
    .pop()
  const trx = splitNaPm
    .filter(str => str.startsWith('trx='))
    .map(str => (str ? str.substr(4) : ''))
    .pop()
  return {
    ci,
    ct,
    tr,
    hk,
    trx,
  }
}

export const getValidNaPm = (naPm: string, NNB: string) => {
  if (!naPm) return null

  const { ci = 'shoppingwindow', ct = getClickTimeForNaPm(), tr, hk, trx = '' } = splitNaPm(naPm)
  if (hk && hk.toLowerCase() === 'dummy') {
    return `ci=${ci}|ct=${ct}|tr=${tr}|hk=${makeHashKeyForNaPm(ct, tr, NNB, ci)}|trx=${trx}`
  }
  if (!isValidHashKey(naPm, NNB)) return null

  return naPm
}

export const getNNBOfCookie = (cookie: string) => {
  const regexResult = cookie && cookie.match(/NNB=(.*?[^;]*)/)
  return regexResult ? regexResult.pop() : ''
}

export const replaceStateWithNaPm = (pathWithNaPm: string) => {
  if (isBrowser) {
    history.replace(pathWithNaPm)
  } else {
    return {
      redirect: pathWithNaPm,
    }
  }
}

export const isValidTrCodeForClickLog = (trCode: TRCode): boolean => {
  if (!trCode) return false
  return [TRCode.MD, TRCode.GDR, TRCode.GLR].includes(trCode)
}
